import { useAuthStore } from '@/stores/authStore';

/**
 * Hook to check if points system is enabled for the current school.
 * Returns true if enabled, false if disabled (default).
 *
 * When points are enabled:
 * - Categories show defaultPoints field
 * - Teachers can override points when logging (if allow_point_override is true)
 * - Reports show net points and point totals
 * - Student profiles show net point scores
 *
 * Note: Points are always stored on records even when disabled (just hidden from UI).
 * This allows enabling/disabling the feature without data loss.
 *
 * IMPORTANT: Points are only meaningful when positive behaviors are enabled,
 * since points calculate net scores between recognitions and incidents.
 */
export function usePointsEnabled(): boolean {
  const tenant = useAuthStore((state) => state.tenant);
  // Points only meaningful when positive behaviors are enabled
  return (
    tenant?.settings?.pointsEnabled === true &&
    tenant?.settings?.positiveBehaviorsEnabled === true
  );
}

/**
 * Hook to get point configuration settings for the current school.
 * Returns configuration values or defaults.
 *
 * Note: pointsEnabled is only true when both pointsEnabled AND
 * positiveBehaviorsEnabled are true, since points calculate net scores.
 */
export function usePointSettings(): {
  pointsEnabled: boolean;
  allowPointOverride: boolean;
  pointOverrideMin: number;
  pointOverrideMax: number;
} {
  const tenant = useAuthStore((state) => state.tenant);
  // Points only meaningful when positive behaviors are enabled
  const pointsEffectivelyEnabled =
    tenant?.settings?.pointsEnabled === true &&
    tenant?.settings?.positiveBehaviorsEnabled === true;

  return {
    pointsEnabled: pointsEffectivelyEnabled,
    allowPointOverride: tenant?.settings?.allowPointOverride !== false, // Default true
    pointOverrideMin: tenant?.settings?.pointOverrideMin ?? 1,
    pointOverrideMax: tenant?.settings?.pointOverrideMax ?? 10,
  };
}
